// React-Query hooks for the admin Embed Sessions report. Backend lives in
// src/api/routers/embed_sessions.py; the wait telemetry it reads is written by
// src/services/embed_telemetry.py off the analysis read path.

import { useQuery } from '@tanstack/react-query';
import ApiService from './api.service';

export type EmbedFlow = 'dashboard' | 'chart' | 'chat';

/** Where a component's rows came from — see EmbedDataSource on the backend. */
export type EmbedDataSource = 'superset' | 'node' | 'build';

/**
 * What produced the wait: a dashboard component fetching data, or Ellie's
 * generate_and_run_analysis tool running inside a chat turn.
 */
export type EmbedEventOrigin = 'dashboard' | 'chat_tool';

export type EmbedSessionSummary = {
  jti: string;
  created_at: string;
  redeemed_at: string | null;
  expires_at: string;
  flow: EmbedFlow;
  target_id: string | null;
  /** Name of the embedded dashboard / chart / conversation, when resolvable. */
  target_label: string | null;
  sub_id: string;
  email: string | null;
  branch: string;
  created_by_api_key_actor: string;
};

export type EmbedSessionPage = {
  records: EmbedSessionSummary[];
  total_records: number;
  total_pages: number;
  page: number;
  page_size: number;
};

export type EmbedDimensionFilterSnapshot = {
  dimension_id: string | null;
  comparator: string | null;
  values: string[];
  /** True for the session's own row-level scope, false for viewer selections. */
  is_user_filter: boolean;
};

/** The date range + filters in effect when a component loaded. */
export type EmbedFilterContext = {
  date_range: string | null;
  date_range_start: string | null;
  date_range_end: string | null;
  user_timezone: string | null;
  previous_period: boolean;
  comparison_type: string | null;
  dimension_filters: EmbedDimensionFilterSnapshot[];
  metric_filter_count: number;
  chart_filter_count: number;
  has_search_query: boolean;
  /** Values or filters were capped — see MAX_SNAPSHOT_* on the backend. */
  truncated: boolean;
};

export type EmbedComponentWait = {
  id: string;
  created_at: string;
  analysis_id: string | null;
  analysis_name: string | null;
  origin: EmbedEventOrigin;
  source: EmbedDataSource;
  superset_id: string | null;
  node_id: string | null;
  /** Null until the orchestration that produced it has finished. */
  build_ms: number | null;
  query_ms: number | null;
  total_wait_ms: number | null;
  row_count: number | null;
  error: string | null;
  filter_context: EmbedFilterContext | null;
};

export type EmbedRowRestriction = {
  dimension: string;
  comparator: string;
  value: string | string[] | null;
  applies_to_metrics: string[];
  unreachable: 'skip' | 'deny';
};

export type EmbedSessionDetail = {
  record: EmbedSessionSummary & {
    role: string;
    ttl_seconds: number;
    code_prefix: string;
    /** Per-dimension allow-list exactly as the embedder supplied it. */
    data_scope: Record<string, string[]>;
    /** data_scope.row_restrictions exactly as the embedder supplied them (names or ids). */
    row_restrictions: EmbedRowRestriction[];
    dimension_filter_ids: string[];
    dimension_names: Record<string, string>;
    blacklisted_metric_ids: string[];
    blacklisted_metric_names: Record<string, string>;
    chat_config: Record<string, any> | null;
  };
  component_waits: EmbedComponentWait[];
  wait_summary: {
    component_count: number;
    slowest_wait_ms: number | null;
    total_query_ms: number | null;
    /** Components whose own dataset had to be built. */
    build_count: number;
    /**
     * Longest orchestration the session sat through. A dashboard refresh
     * reloads nothing until the whole build finishes, so every component
     * waited this out — even ones a fresh superset served.
     */
    shared_build_ms: number | null;
    orchestration_count: number;
    error_count: number;
  };
  /** Chat flows: every conversation this end user drove in the session. */
  thread_ids: string[];
};

export const useEmbedSessions = (
  { page = 1, pageSize = 25, redeemedOnly = false }:
    { page?: number; pageSize?: number; redeemedOnly?: boolean } = {},
) => {
  return useQuery<EmbedSessionPage>({
    queryKey: ['embed-sessions', page, pageSize, redeemedOnly],
    queryFn: async () => {
      const result = await ApiService.getInstance().request('GET', '/embed-sessions', {
        page,
        page_size: pageSize,
        redeemed_only: redeemedOnly,
      });
      return result as EmbedSessionPage;
    },
    staleTime: 30 * 1000,
  });
};

export const useEmbedSession = (jti: string | undefined) => {
  return useQuery<EmbedSessionDetail>({
    queryKey: ['embed-session', jti],
    queryFn: async () => {
      const result = await ApiService.getInstance().request('GET', `/embed-sessions/${jti}`);
      return result as EmbedSessionDetail;
    },
    enabled: !!jti,
    staleTime: 30 * 1000,
  });
};
